import React from 'react';
import { motion } from 'framer-motion';
import { Crown, Target, Zap, Mail, Phone } from 'lucide-react';

export const EliteProgram = () => {
  return (
    <section id="elite-program" className="py-24 bg-[#061324] relative overflow-hidden" data-testid="elite-program-section">
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            'url(https://static.prod-images.emergentagent.com/jobs/a47baca2-6813-4a68-b147-4e346342c0a9/images/5fca53749bc685d6d148ff0cc0697cbba5dcf1d42265b484a01ca7e400a7222a.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#061324] via-transparent to-[#061324]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C9A84C] mb-4">
            <Crown size={16} />
            Flagship Program
          </div>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-white mb-6"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
            data-testid="elite-program-title"
          >
            Elite Player Program
          </h2>
          <p className="text-base md:text-lg text-[#94A3B8] max-w-3xl mx-auto leading-relaxed">
            The highest level of personalized basketball training. This exclusive program is designed for committed athletes ready to transform their game and compete at the elite level.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="bg-[#0A2240] border border-white/10 rounded-sm p-8">
              <h3
                className="text-xl md:text-2xl font-bold uppercase tracking-tight text-white mb-4"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                What You Get
              </h3>
              <ul className="space-y-4">
                {[
                  'Individualized skill development roadmap',
                  'Weekly one-on-one training sessions with Matt Pugh',
                  'Video analysis and film breakdown',
                  'Mental performance and basketball IQ training',
                  'Strength and conditioning integration',
                  'College/Pro recruitment guidance',
                  'Direct communication and ongoing support'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Zap className="text-[#C9A84C] mt-1 flex-shrink-0" size={18} />
                    <span className="text-[#94A3B8]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="bg-[#0A2240] border border-white/10 rounded-sm p-8">
              <h3
                className="text-xl md:text-2xl font-bold uppercase tracking-tight text-white mb-4"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Who This Is For
              </h3>
              <div className="space-y-4 text-[#94A3B8]">
                <p>
                  This program is for serious athletes who are committed to elevating their game. Whether you&apos;re preparing for college basketball, professional opportunities, or simply want to maximize your potential—this is your path.
                </p>
                <div className="pt-4 border-t border-white/10">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="text-[#C9A84C]" size={20} />
                    <span className="font-bold text-white">Limited Availability</span>
                  </div>
                  <p className="text-sm">
                    To maintain the highest quality of training, spots are limited. This ensures each athlete receives the focused attention necessary for elite development.
                  </p>
                </div>
              </div>
            </div>

            <div
              className="relative bg-[#061324] rounded-sm overflow-hidden"
            >
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage:
                    'url(https://static.prod-images.emergentagent.com/jobs/a47baca2-6813-4a68-b147-4e346342c0a9/images/d8c206479843e74e32dd8d78366cdae4f927a38eda53fcd6d069da0ce99c4724.png)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-br from-[#C9A84C]/20 to-[#d8b655]/20" />
              <div className="relative z-10 p-8">
                <h3 className="text-2xl font-black uppercase mb-4 text-[#C9A84C]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Ready to Elevate?
                </h3>
                <p className="mb-6 text-white font-medium">
                  Contact Matt directly to discuss your goals and see if the Elite Player Program is the right fit.
                </p>
                <div className="space-y-3">
                  <a
                    href="mailto:mpugh@puresweatbasketball.com"
                    className="flex items-center gap-3 bg-[#C9A84C] text-[#061324] px-6 py-3 rounded-sm font-bold uppercase tracking-wide text-sm hover:bg-[#d8b655] transition-colors"
                    data-testid="elite-email-button"
                  >
                    <Mail size={18} />
                    mpugh@puresweatbasketball.com
                  </a>
                  <a
                    href="sms:+15734330599"
                    className="flex items-center gap-3 bg-[#C9A84C] text-[#061324] px-6 py-3 rounded-sm font-bold uppercase tracking-wide text-sm hover:bg-[#d8b655] transition-colors"
                    data-testid="elite-phone-button"
                  >
                    <Phone size={18} />
                    (573) 433-0599
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};